import { useEffect, useMemo, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_API || "";

const pad2 = (n) => String(n).padStart(2, "0"); // helper for String

function formatTimeLocal(iso) { // function for Time like showing 12:12 AM
  const d = new Date(iso);
  let h = d.getHours();
  const m = pad2(d.getMinutes());
  const am = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${pad2(h)}:${m} ${am}`;
}

function minutesDiff(a, b) {
  return Math.round((a.getTime() - b.getTime()) / 60000); // function for minute difference (use Math.round function)
}

function paginate(arr, size) { // splits an array into pages
  if (size <= 0) return [arr];
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out.length ? out : [[]];
}

export default function useClassroomFeed({ // Hook Signature
  pollMs = 30000,
  upcomingWindowMin = 30,
  rooms = undefined,

  cols = 4,
  ongoingRows = 1,
  upcomingRows = 1,
  rotateMs = 30000,
} = {}) {
  const [raw, setRaw] = useState([]); // [{ room, title, start, end, location, email }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [roomsFilter, setRoomsFilter] = useState(rooms);
  const pollRef = useRef(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 1e9), 10000); // Force recalculation every 10s
    return () => clearInterval(id);
  }, []);

  const [carouselPage, setCarouselPage] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCarouselPage((p) => (p + 1) % 1e9); // Automatically rotate visible events
    }, rotateMs);
    return () => clearInterval(id); // Clear the interval
  }, [rotateMs]);

  function buildQuery() { // Fetches today's schedule only
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);

    const qs = new URLSearchParams({
      from: start.toISOString(),
      to: end.toISOString(),
    });

    if (roomsFilter?.length) qs.set("rooms", roomsFilter.join(","));
    return `${API_BASE}/roomSchedules?${qs.toString()}`;
  }

  async function load() { // Fetches schedule data
    try {
      setError("");
      const url = buildQuery();
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Unknown error");

      // clean + sort
      const cleaned = (json.data || []).filter(
        (e) =>
          e.room &&
          e.start &&
          e.end &&
          !isNaN(new Date(e.start)) &&
          !isNaN(new Date(e.end))
      );
      cleaned.sort((a, b) => new Date(a.start) - new Date(b.start));
      setRaw(cleaned);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { //polling Effect
    load();
    pollRef.current = setInterval(load, pollMs);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollMs, roomsFilter?.join(",")]);

  // eslint-disable-next-line
  const now = useMemo(() => new Date(), [tick, raw.length]); //current time Memoization

  function formatDuration(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h} hr ${m} min`;
    if (h > 0) return `${h} hr`;
    return `${m} min`;
  }

  const { ongoing, upcoming, earlier, allToday } = useMemo(() => {
    const ongoing = [];
    const upcoming = [];
    const earlier = [];
    const TOL_MS = 2 * 60 * 1000; // 2-minute tolerance

    for (const ev of raw) {
      const start = new Date(ev.start);
      const end = new Date(ev.end);

      if (
        start.getTime() - TOL_MS <= now.getTime() &&
        now.getTime() < end.getTime() + TOL_MS
      ) {
        const minsLeft = Math.max(0, minutesDiff(end, now));
        ongoing.push({
          room: ev.room,
          title: ev.title || "(No title)",
          time: `${formatTimeLocal(start)} – ${formatTimeLocal(end)}`,
          statusText:
            minsLeft <= 1
              ? "Course ending now"
              : `Class ends in ${formatDuration(minsLeft)}`,
          start: ev.start,
          end: ev.end,
          location: ev.location || "",
          email: ev.email,
        });
      } else if (now < start) {
        const minsTo = minutesDiff(start, now);
        upcoming.push({
          room: ev.room,
          title: ev.title || "(No title)",
          time: `${formatTimeLocal(start)} – ${formatTimeLocal(end)}`,
          statusText:
            minsTo <= 1
              ? "Course starting now"
              : `Class starts in ${formatDuration(minsTo)}`,
          highlighted: minsTo <= 5,
          start: ev.start,
          end: ev.end,
          location: ev.location || "",
          email: ev.email,
        });
      } else {
        earlier.push({
          room: ev.room,
          title: ev.title || "(No title)",
          time: `${formatTimeLocal(start)} – ${formatTimeLocal(end)}`,
          statusText: "Completed",
          start: ev.start,
          end: ev.end,
          location: ev.location || "",
          email: ev.email,
        });
      }
    }

    // sort sections
    ongoing.sort((a, b) => new Date(a.end) - new Date(b.end));
    upcoming.sort((a, b) => new Date(a.start) - new Date(b.start));
    earlier.sort((a, b) => new Date(a.start) - new Date(b.start));

    const allToday = [...earlier, ...ongoing, ...upcoming];
    // eslint-disable-next-line
    return { ongoing, upcoming, earlier, allToday };
    // eslint-disable-next-line
  }, [raw, now, upcomingWindowMin]);

  // ---------- NEW: compute rotating “visible” slices ----------
  const visiblePerOngoing = Math.max(0, (cols || 1) * (ongoingRows || 0));
  const visiblePerUpcoming = Math.max(0, (cols || 1) * (upcomingRows || 0));

  const visibleOngoing = useMemo(() => {
    if (!visiblePerOngoing) return [];
    const pages = paginate(ongoing, visiblePerOngoing);
    const idx = pages.length ? carouselPage % pages.length : 0;
    return pages[idx];
  }, [ongoing, visiblePerOngoing, carouselPage]);

  const visibleUpcoming = useMemo(() => {
    if (!visiblePerUpcoming) return [];
    const pages = paginate(upcoming, visiblePerUpcoming);
    const idx = pages.length ? carouselPage % pages.length : 0;
    return pages[idx];
  }, [upcoming, visiblePerUpcoming, carouselPage]);

  return {
    // original
    loading,
    error,
    lastUpdated,
    ongoing,
    upcoming,
    earlier,
    allToday,
    refresh: load,
    setRoomsFilter,

    visibleOngoing,
    visibleUpcoming,
    carouselPage,
  };
}
